import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EnvironmentInjector,
  HostListener,
  inject,
  Inject,
  Injector,
  OnInit,
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationStart,
  Router,
  RouterModule,
} from '@angular/router';
import { ModalService } from '@indice/ng-components';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { UserProfile } from 'oidc-client-ts';
import { Observable, of, Subject, switchMap } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { HeaderService } from 'src/app/services/header.service';
import { NotificationsService } from 'src/app/services/notifications.service';
import {
  Notifications,
  PropertyListFilter,
  PropertyType,
} from 'src/app/services/portal-api.service';
import { ClickOutsideDirective } from 'src/app/shared/directives/click-outside.directive';
import * as environment from '../../../core/appSettings';
import { OnboardingExitModalComponent } from '../../../features/onboarding/onboarding-exit-modal/onboarding-exit-modal.component';
import { AuthService } from './../../../core/services/auth.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DEMO_ROLE_CLAIM } from 'src/app/core/services/guard-services/demo-guard.service';
import { localizeRoute } from 'src/app/shared/utilities/localizeRoutes';
import {
  FilterDropdownItem,
} from 'src/app/features/find-property/filter-dropdown/filter-dropdown.component';
import {
  RangeDropdownItem,
} from 'src/app/features/find-property/range-dropdown/range-dropdown.component';
import { PropertyListingService } from 'src/app/services/property.service';
import { LanguageService } from 'src/app/core/services/language.service';
import { extractValidRoutes } from 'src/app/core/services/guard-services/custom-redirect-guard.service';

@Component({
  selector: 'app-navigation',
  standalone: true,
  imports: [
    RouterModule,
    CommonModule,
    TranslocoDirective,
    ClickOutsideDirective,
  ],
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss'],
})
export class NavigationComponent implements OnInit {
  isLoggedIn = false;
  greeting: string | undefined;
  fullName: string | undefined;
  readonly authorityUrl!: string;
  // Boolean to control dynamic header classes
  public headerFixed = false;
  alternav = this._headerService.IsAltNav$;
  alternavTitle!: Observable<string | null>;
  alternavTitleTranslation!: string;
  isHomePage = false;
  listingNav = false;
  listingNavTitle = '';
  public currentRoute: string = '';
  public authUrl = environment.getAppSettings(this.envInjector).auth_settings
    .authority;
  public isMobileDevice?: boolean;
  public hideDot: boolean = true; // The green dot on the Avatar
  public numberOfRequests: number = 0; // Number of Requests shown in the Avatar Dropdown menu

  // Current year
  public copyrightYear = new Date().getFullYear();
  public isMobileMenuOpen = false;
  public isProfileMenuOpen = false;
  public notifications: Notifications = new Notifications();
  showUserMenu = false;

  isAnyFilterSelected = false;

  private isAdvancedFilter(key: string): boolean {
    const keys = [
      'type',
      'minPrice',
      'maxPrice',
      'minSqMeters',
      'maxSqMeters',
      'searchTerm',
    ];
    return !keys.includes(key);
  }
  private hasAreaSearch = false;

  showDropdown = false;
  openDropdowns: { [key: string]: boolean } = {};
  showLangDropdown = false;

  activeLang: string = '';

  localizeRoute = localizeRoute;

  searchFilter: PropertyListFilter | undefined;
  searchFiltersCount: number = 0;

  propertyTypes: FilterDropdownItem[] = [];
  readonly allowedPropertyTypes: PropertyType[] = [
    PropertyType.Apartment,
    PropertyType.Villa,
    PropertyType.Maisonette,
    PropertyType.CountryHouse,
  ];

  areaUnit!: string;
  selectedType: PropertyType[] | undefined;

  minPriceRanges: RangeDropdownItem[] = [
    { from: 10000, label: '10.000€' },
    { from: 20000, label: '20.000€' },
    { from: 30000, label: '30.000€' },
    { from: 40000, label: '40.000€' },
    { from: 50000, label: '50.000€' },
  ];

  maxPriceRanges: RangeDropdownItem[] = [
    { to: 100000, label: '100.000€' },
    { to: 200000, label: '200.000€' },
    { to: 500000, label: '500.000€' },
    { to: 1000000, label: '1.000.000€' },
    { to: 2000000, label: '2.000.000€' },
  ];

  minSizeRanges: RangeDropdownItem[] = [
    { from: 10, label: '10 τ.μ.' },
    { from: 20, label: '20 τ.μ.' },
    { from: 30, label: '30 τ.μ.' },
    { from: 40, label: '40 τ.μ.' },
    { from: 50, label: '50 τ.μ.' },
  ];

  maxSizeRanges: RangeDropdownItem[] = [
    { to: 150, label: '150 τ.μ.' },
    { to: 200, label: '200 τ.μ.' },
    { to: 500, label: '500 τ.μ.' },
    { to: 800, label: '800 τ.μ.' },
    { to: 1000, label: '1000 τ.μ.' },
  ];

  headerNavigation = [
    {
      label: 'anchors.buy',
      icon: 'icon-buy-property',
      link: '/properties',
      showOnMobile: true,
    },
    {
      label: 'anchors.sell',
      icon: 'icon-sell-property',
      link: '/sell-property-get-started',
      showOnMobile: true,
    },
    {
      label: 'anchors.financing',
      icon: 'icon-investment',
      link: '/financing',
      showOnMobile: true,
    },
    {
      label: 'anchors.more',
      icon: undefined,
      link: undefined,
      showOnMobile: false,
    },
  ];

  selectedPriceRange: RangeDropdownItem | undefined;
  selectedSizeRange: RangeDropdownItem | undefined;
  toggleAdvancedFilters$: Subject<void> = new Subject();
  advancedFilterOn: boolean = false;

  private readonly emptySearchFilters = {
    from: undefined,
    to: undefined,
    label: '',
  };

  mainMenu = [
    {
      label: 'anchors.buy',
      icon: 'icon-buy-property',
      extraSection: false,
      link: undefined,
      showOnMobile: true,
      children: [
        {
          label: 'anchors.legalReport',
          link: '/services/legalReport',
        },
        {
          label: 'anchors.propertyValuation',
          link: '/services/technicalReport',
        },
        {
          label: 'anchors.biddingSupport',
          link: '/services/biddingSupport',
        }
      ],
    },
    {
      label: 'anchors.sell',
      icon: 'icon-sell-property',
      extraSection: false,
      link: undefined,
      showOnMobile: true,
      children: [
        {
          label: 'anchors.propertyAccreditation',
          link: '/services/preapproval',
        },
        {
          label: 'anchors.legalReport',
          link: '/services/legalReport',
        },
        {
          label: 'anchors.propertyValuation',
          link: '/services/technicalReport',
        },
        {
          label: 'anchors.propertyFileConstitution',
          link: '/services/propertyFileConstitution',
        },
        {
          label: 'anchors.propertyPhotoService',
          link: '/services/photography',
        },
      ],
    },
    {
      label: 'anchors.financing',
      icon: 'icon-investment',
      extraSection: false,
      link: undefined,
      showOnMobile: true,
      children: [
        {
          label: 'anchors.mortgage',
          link: '/financing',
        },
        {
          label: 'anchors.renovation',
          link: '/financing',
        },
      ],
    },
    {
      label: 'anchors.discoverUniko',
      icon: undefined,
      extraSection: true,
      link: undefined,
      showOnMobile: false,
      children: [
        {
          label: 'anchors.allServices',
          link: '/services',
        },
        {
          label: 'anchors.aboutUs',
          link: '/about',
        },
        {
          label: 'anchors.contactUs',
          link: '/contact',
        },
        {
          label: 'anchors.faqs',
          link: '/faq',
        },
      ],
    },
  ];
  profileMenu = [
    {
      label: 'anchors.myRecords',
      icon: 'icon-house',
      link: '/my-profile/listings',
    },
    {
      label: 'anchors.favorites',
      icon: 'icon-heart-line',
      link: '/my-profile/favourites',
    },
    {
      label: 'anchors.offers',
      icon: 'icon-investment',
      link: '/my-profile/buying',
    },
    {
      label: 'anchors.requests',
      icon: 'icon-list-todo',
      link: '/my-profile/requests',
    },
    {
      label: 'anchors.savedSearches',
      icon: 'icon-bookmark',
      link: '/account-settings/saved-searches',
    },
  ];
  closeReturnUrl: string = '/home';

  private destroyRef = inject(DestroyRef);

  private destroyed$ = new Subject();
  private altNavCancelProgress = true;

  constructor(
    private modalService: ModalService,
    private _authService: AuthService,
    private router: Router,
    private _translate: TranslocoService,
    private _notifications: NotificationsService,
    private _headerService: HeaderService,
    private _transloco: TranslocoService,
    private activatedRoute: ActivatedRoute,
    private propertyListingService: PropertyListingService,
    private injector: Injector,
    @Inject(EnvironmentInjector) public envInjector: EnvironmentInjector,
    private languageService: LanguageService
  ) {
    this.router.events
      .pipe(
        filter((event) => {
          return event instanceof NavigationStart;
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe((event) => {
        this.closeAllMenus();
      });
  }

  ngOnInit(): void {
    this.activeLang = this._transloco.getActiveLang();
    this._headerService.HeaderAltTitle$.pipe(
      takeUntilDestroyed(this.destroyRef),
      tap((translationKey) => {
        return of(translationKey);
      }),
      switchMap((translation: string) => {
        return this._translate.selectTranslateObject(`${translation}`);
      })
    ).subscribe(
      (translation) =>
      (this.alternavTitleTranslation =
        translation ?? 'Εκδήλωση ενδιαφέροντος για χρηματοδότηση')
    );

    this.propertyTypes = this.allowedPropertyTypes.map((type) => {
      return {
        label: `find-property.propertyType.${type}`,
        key: type,
        icon: this.propertyTypeIcons(type),
        translation: `find-property.propertyType.${type}`,
      };
    });

    this.activatedRoute.queryParamMap
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        this.searchFilter =
          this.propertyListingService.getSearchFiltersFromUrl(params);

        this.searchFiltersCount = this.countSelectedFilters(
          this.searchFilter,
          this.hasAreaSearch,
          true
        ).count;

        //Type filter
        this.selectedType = this.searchFilter.type?.length
          ? this.searchFilter.type
          : [];

        if (this.searchFilter.minSqMeters || this.searchFilter.maxSqMeters) {
          let label = `${this.searchFilter.minSqMeters?.toString() ?? ''} - ${this.searchFilter.maxSqMeters?.toString() ?? ''
            }`;
          this.selectedSizeRange = {
            from: this.searchFilter.minSqMeters,
            to: this.searchFilter.maxSqMeters,
            label,
          };
        } else {
          this.selectedSizeRange = this.emptySearchFilters;
        }
      });

    this.getUser();

    const userLoaded$ = this._authService.userLoaded;
    userLoaded$
      .pipe(
        // Use switchMap to handle the user loading
        switchMap((user) => {
          if (user) {
            this.isLoggedIn = true;
            this.fullName = `${user.profile?.given_name} ${user.profile?.family_name ?? ''
              }`;
            return this._authService.getUserProfile().pipe(
              switchMap((response: UserProfile | undefined) => {
                if (response) {
                  this.greeting = response.name || response.email;
                  this._notifications.refreshNotifications();
                  return this._notifications.notifications$.pipe(
                    startWith(undefined)
                  );
                } else {
                  return of(undefined);
                }
              })
            );
          } else {
            return of(undefined);
          }
        }),
        tap((notifications: Notifications | undefined) => {
          if (notifications) {
            this.notifications = notifications;
          }
        }),
        catchError((err) => {
          console.error('Error fetching notifications:', err);
          return of(undefined); // Handle error gracefully
        })
      )
      .subscribe();
    this._headerService.HeaderAltReturnUrl$.subscribe((x) => {
      this.closeReturnUrl = x;
    });
    // Get current route

    // Skip 'Cancel Progress' validation when closing the wizard on the final step (eg checkout/summary),
    // as there's no need to preserve progress at this stage.
    this._headerService.altNavCancelProgress$
      .pipe(
        tap((cancelProgressResult) => {
          this.altNavCancelProgress = cancelProgressResult;
        })
      )
      .subscribe();

    this.isMobileDevice = this.isMobile();
  }

  isMobile(): boolean {
    return window.innerWidth <= 1024;
  }

  handleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  closeMobileMenu() {
    if (!this.isMobileDevice) {
      this.isMobileMenuOpen = false;
    }
  }

  handleProfileMenu(): void {
    this.isProfileMenuOpen = !this.isProfileMenuOpen;
  }

  linkAndClose(route: string): string {
    this.closeAllMenus();
    return route;
  }

  navigateAndClose(route?: string) {
    if (!route) {
      return this.closeAllMenus();
    }

    const currentLang = this._translate.getActiveLang();
    this.router.navigate([localizeRoute(route, this.envInjector)]).then(() => {
      this.closeAllMenus();
    });
  }

  closeProfileMenu() {
    if (!this.isMobileDevice) {
      this.isProfileMenuOpen = false;
    }
  }

  navigateToLoginWithRedirect() {
    this.currentRoute = this.router.url;
    localStorage.setItem('redirectRoute', this.currentRoute);
    this.navigateAndClose('/login');
  }

  login(): void {
    const currRoute = this.router.url;
    this._authService.signinRedirect(currRoute);
  }

  logout(): void {
    var userInfo = this._authService.getCurrentUser();
    const demoRoleClaim = userInfo?.profile[DEMO_ROLE_CLAIM] as string;
    if (demoRoleClaim) {
      return;
    }
    this._authService.signoutRedirect();
  }

  public changeLanguage(lang: string) {
    this._translate.setActiveLang(lang);
    this.languageService.setCurrentLang(lang);

    this.activeLang = lang;
    this.showLangDropdown = false;

    const urlTree = this.router.parseUrl(this.router.url);
    
    const validRoutes = extractValidRoutes();
    if (!validRoutes.includes(this.getPathAfterLang(this.router.url).pathAfterLang)) {
      let navigateToUrl = this.router.url.replace(this.getPathAfterLang(this.router.url).lang, this._translate.getActiveLang()) // this needs reviewing
      this.router.navigateByUrl(navigateToUrl);
    } else {
      const routeSegment = this.getPathAfterLang(this.router.url);

      const localizedRoute = localizeRoute(
        `/${routeSegment.pathAfterLang}`,
        this.envInjector
      ); // Call localizeRoute to trigger the URL update

      // Strip query parameters from localizedRoute
      const localizedRouteUrl = new URL(localizedRoute, window.location.origin); // Use a base URL for parsing
      localizedRouteUrl.search = ''; // Remove query string
      const cleanedLocalizedRoute = localizedRouteUrl.pathname;

      const newUrlTree = this.router.createUrlTree([cleanedLocalizedRoute], {
        // Use the localized route directly
        queryParams: urlTree.queryParams,
        fragment: urlTree.fragment as string | undefined,
      });

      const newUrl = this.router.serializeUrl(newUrlTree);

      this.router.navigateByUrl(newUrl);
    }
  }

  /**
   * @description: Function to add dynamic classes to header
   * @param: none
   * @returns: none
   * @memberof NavigationComponent
   */
  @HostListener('window:scroll', ['$event']) onScroll() {
    if (window.pageYOffset > 0) {
      this.headerFixed = true;
    } else {
      this.headerFixed = false;
    }
  }

  handleShowDropdown(id: string): void {
    this.showDropdown = !this.showDropdown;
    this.openDropdowns[id] = !this.openDropdowns[id];
  }

  public goToFavorites(): void {
    this.router.navigateByUrl(
      localizeRoute('/properties?favorites=1', this.envInjector)
    );
  }

  openModalComponent() {
    if (this.altNavCancelProgress) {
      const exitModal = this.modalService.show(OnboardingExitModalComponent, {
        animated: true,
        keyboard: true,
        class: 'h-dvh md:h-auto',
      });
      exitModal.onHidden?.subscribe((response: any) => {
        if (response.result.nav == true) {
          if (
            this.router.url.includes('onboarding') ||
            this.router.url.includes('welcome')
          ) {
            this._authService.signinRedirect(
              localizeRoute('/home', this.envInjector)
            );
          }
          this.router
            .navigate([localizeRoute(this.closeReturnUrl, this.envInjector)])
            .then((_) => {
              this._headerService.clearReturnUrl();
            });
        }
      });
    } else {
      this.router
        .navigate([localizeRoute(this.closeReturnUrl, this.envInjector)])
        .then((_) => this._headerService.showCancelProgress(true))  // # Reactivate "Cancel Progress" validation when closing the wizard, to restore the original logic for the next service purchase flow.
        .then((_) => {
          this._headerService.clearReturnUrl();
        });
    }
  }

  private closeAllMenus() {
    this.showDropdown = false;
    this.isProfileMenuOpen = false;
    this.isMobileMenuOpen = false;
    this.showUserMenu = false;
  }

  private getUser() {
    this._authService
      .isLoggedIn()
      .pipe(
        map((res) => {
          this.isLoggedIn = res;

          if (this.isLoggedIn) {
            this._authService
              .getUserProfile()
              .pipe(
                map((profile) => {
                  this.greeting = profile?.name || profile?.email;
                  this.fullName = `${profile?.given_name} ${profile?.family_name ?? ''
                    }`;
                })
              )
              .subscribe();
          }
        })
      )
      .subscribe();
  }

  private propertyTypeIcons(
    propertyType: PropertyType | undefined
  ): string | undefined {
    const defaultIcon = 'icon-Apartment';

    switch (propertyType) {
      case PropertyType.Apartment:
        return 'icon-Apartment';
      case PropertyType.Maisonette:
        return 'icon-maisonette';
      case PropertyType.Business:
        return 'icon-briefcase';
      case PropertyType.Villa:
        return 'icon-villas';
      default:
        return defaultIcon;
    }
  }

  searchWithTypeFilter(propertyType: any | undefined) {
    if (!propertyType || propertyType.length == 0) {
      this.router.navigate([localizeRoute('/properties', this.envInjector)], {
        queryParams: { type: undefined },
        queryParamsHandling: 'merge',
      });
      return;
    }

    let filter: PropertyListFilter = new PropertyListFilter({
      type: propertyType.join(','),
    });
    this.router.navigate([localizeRoute('/properties', this.envInjector)], {
      queryParams: { ...filter, page: 1 },
      queryParamsHandling: 'merge',
    });
  }
  searchWithPriceFilter(range: RangeDropdownItem | undefined) {
    if (!range) {
      this.router.navigate([localizeRoute('/properties', this.envInjector)], {
        queryParams: { minPrice: undefined, maxPrice: undefined },
        queryParamsHandling: 'merge',
      });
      return;
    }

    let filter: PropertyListFilter = new PropertyListFilter({
      minPrice: range.from,
      maxPrice: range.to,
    });

    this.router.navigate([localizeRoute('/properties', this.envInjector)], {
      queryParams: { ...filter, page: undefined },
      queryParamsHandling: 'merge',
    });
  }
  searchWithSizeFilter(range: RangeDropdownItem | undefined) {
    if (!range) {
      this.router.navigate([localizeRoute('/properties', this.envInjector)], {
        queryParams: { minSqMeters: undefined, maxSqMeters: undefined },
        queryParamsHandling: 'merge',
      });
      return;
    }

    let filter: PropertyListFilter = new PropertyListFilter({
      minSqMeters: range.from,
      maxSqMeters: range.to,
    });

    this.router.navigate([localizeRoute('/properties', this.envInjector)], {
      queryParams: { ...filter, page: undefined },
      queryParamsHandling: 'merge',
    });
  }

  toggleAdvancedFilers() {
    this.toggleAdvancedFilters$.next();
    this.advancedFilterOn = !this.advancedFilterOn;
    document.documentElement.classList.toggle('overflow-hidden');
  }

  private countSelectedFilters(
    filterObj: { [key: string]: any },
    hasAreaSearch: boolean,
    onlyAdvanced: boolean = false
  ): { count: number; filter: { [key: string]: any } } {
    let count = 0;

    for (const key in filterObj) {
      if (onlyAdvanced && !this.isAdvancedFilter(key)) {
        continue;
      }
      const value = filterObj[key];

      // Count each element in arrays
      if (Array.isArray(value)) {
        count += value.length; // Add the length of the array to the count
      }
      // Count boolean values if they are true
      else if (typeof value === 'boolean' && value === true) {
        count++;
      }
      // Count numbers (assuming any specified number is considered a selected filter)
      else if (typeof value === 'number') {
        count++;
      } else if (typeof value === 'string') {
        if (key !== 'area' || (key === 'area' && this.hasAreaSearch)) {
          count++;
        }
      }
    }

    return {
      count: count,
      filter: filterObj,
    };
  }

  private getPathAfterLang(url: string) {
    const parts = url.split('/');
    const langIndex = 1; // Assuming the language code is always the second part
    const pathAfterLang = parts.slice(langIndex + 1).join('/').split('?')[0];
    return { pathAfterLang: pathAfterLang, lang: parts[langIndex] };
  }
}

